<template>
  <ReservationDetailSkeleton
    v-if="state === STATE.EDITING_RESERVATION"
    id="reservation-edit-loader"
  />
  <SlotPanel v-else id="reservation-edit" classes="relative">
    <div class="relative mb-6">
      <div id="reservation-name" class="font-heading font-semibold text-4xl grow w-11/12">
        {{ displayName }}
      </div>
      <ReservationStatusBadge
        v-if="selectedReservation"
        :status="selectedReservation.status"
        class="-translate-y-1/2 translate-x-3/4 absolute top-1/2 right-0"
      />
    </div>

    <ReservationGuestCount v-model="totalGuests" :minGuests="minGuests" :maxGuests="maxGuests" />
    <OnClickOutside
      id="seating-time-dropdown"
      class="-mt-2.5 relative"
      v-if="selectedSeatingTime"
      @trigger="isSeatingTimeDropdownOpen = false"
    >
      <span
        id="seat-time-dropdown-button"
        class="text-fg-link-primary cursor-pointer"
        @click="isSeatingTimeDropdownOpen = !isSeatingTimeDropdownOpen"
      >
        {{ timeStr(selectedSeatingTime.time) }} Seating Time
        <ChevronDown class="stroke-blue-10 inline w-4 h-4" />
      </span>
      <div
        v-if="isSeatingTimeDropdownOpen"
        id="seating-time-dropdown-menu"
        class="absolute w-52 bg-surface-primary shadow-podHover rounded flex flex-col border border-linear-subtle"
      >
        <span
          v-for="seatingTime in seatingTimes"
          :key="seatingTime.id"
          :id="`seating-time-item-${seatingTime.id}`"
          @click="selectSeatingTime(seatingTime)"
          class="hover:bg-surface-base transition-all cursor-pointer px-2 py-2"
          :class="{ 'bg-surface-base font-semibold': seatingTimeId == seatingTime.id }"
        >
          {{ timeStr(seatingTime.time) }}
        </span>
      </div>
    </OnClickOutside>
    <div class="mt-4">
      <TextareaInput id="reservation-edit-notes" label="Notes" v-model="notes" />
    </div>
    <ToggleTagList
      v-if="reservation_tags.length > 0"
      :tagList="reservation_tags"
      id="reservation-edit-tags"
      v-model="tags"
      label="Reservation Tags"
      class="mt-4"
    />
    <ErrorDisplay id="error-display" v-bind="ui.error" class="mt-4" />
    <div class="flex items-center justify-between mt-6">
      <div class="w-1/2 pr-2">
        <SlotButton id="close-button" @click="closeReservationEdit" class="w-full" alt>
          Cancel
        </SlotButton>
      </div>
      <div class="w-1/2 pl-2">
        <SlotButton
          id="save-button"
          class="w-full"
          @click="editReservation"
          :disabled="isSaveButtonDisabled"
        >
          Save
        </SlotButton>
      </div>
    </div>
  </SlotPanel>
</template>

<script lang="ts">
import { OnClickOutside } from '@vueuse/components'
import { mapState, mapStores } from 'pinia'
import { defineComponent } from 'vue'

import ChevronDown from '@/assets/chevronDown.svg?component'
import ErrorDisplay from '@/common/components/atoms/ErrorDisplay.vue'
import SlotButton from '@/common/components/atoms/SlotButton.vue'
import TextareaInput from '@/common/components/molecules/TextareaInput.vue'
import ToggleTagList from '@/common/components/molecules/ToggleTagList.vue'
import SlotPanel from '@/common/components/organisms/SlotPanel.vue'
import { usePerformanceReservationStore } from '@/modules/performance/stores/PerformanceReservationStore'
import ReservationStatusBadge from '@/modules/reservation/components/atoms/ReservationStatusBadge.vue'
import ReservationGuestCount from '@/modules/reservation/components/molecules/ReservationGuestCount.vue'
import ReservationDetailSkeleton from '@/modules/reservation/components/organisms/ReservationDetailSkeleton.vue'
import { useReservationStore } from '@/modules/reservation/stores/ReservationStore'
import { useSettingStore } from '@/stores/setting'
import { blankError, clearError, displayHandled } from '@/utils/errors'
import { byId, sortBy, sum } from '@/utils/utils'

import { PerformanceSeatingTimeRead, ReservationTagId } from '@generated/types'

const STATE = {
  RESERVATION_FORM: 'reservation-form',
  EDITING_RESERVATION: 'editing-reservation',
}

export default defineComponent({
  name: 'ReservationEdit',
  components: {
    ToggleTagList,
    ReservationGuestCount,
    ReservationStatusBadge,
    ReservationDetailSkeleton,
    ErrorDisplay,
    TextareaInput,
    ChevronDown,
    SlotButton,
    SlotPanel,
    OnClickOutside,
  },
  emits: {
    close: () => true,
    reservationEdited: () => true,
  },
  data() {
    return {
      state: STATE.RESERVATION_FORM,
      isSeatingTimeDropdownOpen: false,
      totalGuests: 0,
      seatingTimeId: 0,
      notes: '',
      tags: [] as ReservationTagId[],
      ui: { error: blankError() },
      STATE,
    }
  },
  mounted() {
    this.totalGuests = this.selectedReservation!.number_of_guests
    this.seatingTimeId = this.selectedReservation!.seating_time_id
    this.notes = this.selectedReservation!.internal_notes
    this.tags = this.selectedReservation!.tags
  },
  methods: {
    closeReservationEdit() {
      this.$emit('close')
    },
    selectSeatingTime(seatingTime: PerformanceSeatingTimeRead) {
      this.seatingTimeId = seatingTime.id
      this.isSeatingTimeDropdownOpen = false
    },
    async editReservation() {
      if (this.isSaveButtonDisabled) return
      clearError(this.ui.error)

      try {
        this.state = STATE.EDITING_RESERVATION
        await this.reservationStore.updateReservation({
          id: this.selectedReservation!.id,
          seating_time: this.seatingTimeId,
          number_of_guests: this.totalGuests,
          internal_notes: this.notes,
          tables: this.selectedTableIds,
          tags: this.tags,
        })
        this.$emit('reservationEdited')
      } catch (error) {
        displayHandled(error, this.ui.error)
      } finally {
        this.state = STATE.RESERVATION_FORM
      }
    },
    updateTotalGuests(value: number): void {
      if (value > 0) this.totalGuests = value
    },
  },
  computed: {
    ...mapStores(useReservationStore),
    ...mapState(useSettingStore, ['timeStr', 'reservation_tags']),
    ...mapState(usePerformanceReservationStore, [
      'selectedPerformance',
      'selectedReservation',
      'selectedTableIds',
      'selectedTables',
      'hasSelectedTables',
    ]),
    minGuests(): number {
      if (this.selectedTables.length === 0) return 0
      if (this.selectedTables.length === 1) return this.selectedTables[0].minimum_guests

      const allMaxGuests = sum(this.selectedTables.map(table => table.maximum_guests))
      const cheapestTable = sortBy(this.selectedTables.slice(), 'price_per_person')[0]
      return allMaxGuests - cheapestTable.maximum_guests + 1
    },
    maxGuests(): number {
      return sum(this.selectedTables.map(table => table.maximum_guests))
    },
    selectedSeatingTime(): PerformanceSeatingTimeRead | undefined {
      return this.selectedPerformance!.seating_times.find(byId(this.seatingTimeId))
    },
    seatingTimes(): PerformanceSeatingTimeRead[] {
      return this.selectedPerformance!.seating_times
    },
    isSaveButtonDisabled(): boolean {
      return !this.hasSelectedTables || this.totalGuests <= 0
    },
    reservedGuests(): number {
      const guests = sum(this.selectedTables.map(table => table.maximum_guests))
      return Math.min(guests, this.totalGuests)
    },
    displayName(): string {
      if (!this.selectedReservation!.contact) {
        return 'Walk In'
      }
      return this.selectedReservation!.contact.name
    },
  },
})
</script>
